"use client";

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { Permission } from "@/lib/rbac";

interface RouteGuardProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
  allowedStoreTypes?: string[];
  redirectTo?: string;
  fallback?: React.ReactNode;
}

/**
 * Component to protect entire pages/routes
 * Redirects unauthorized users or shows fallback content
 */
export function RouteGuard({
  children,
  requiredPermissions = [],
  allowedStoreTypes = [],
  redirectTo,
  fallback,
}: RouteGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { hasPermission, storeType, currentStore } = usePermissions();

  useEffect(() => {
    // Check store type restrictions
    if (
      allowedStoreTypes.length > 0 &&
      storeType &&
      !allowedStoreTypes.includes(storeType)
    ) {
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
        return;
      }
    }

    // Check required permissions
    if (requiredPermissions.length > 0) {
      const hasAllPermissions = requiredPermissions.every((permission) =>
        hasPermission(permission),
      );

      if (!hasAllPermissions) {
        if (redirectTo) {
          navigate(redirectTo, { replace: true });
          return;
        }
      }
    }
  }, [
    storeType,
    currentStore,
    pathname,
    requiredPermissions,
    allowedStoreTypes,
    redirectTo,
    navigate,
    hasPermission,
  ]);

  // Show loading or fallback while checking permissions
  if (!currentStore) {
    return (
      fallback || (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      )
    );
  }

  // Check store type restrictions
  if (
    allowedStoreTypes.length > 0 &&
    storeType &&
    !allowedStoreTypes.includes(storeType)
  ) {
    return (
      fallback || (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">🚫</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Access Restricted
            </h1>
            <p className="text-gray-600">
              This feature is not available for {storeType} accounts.
            </p>
          </div>
        </div>
      )
    );
  }

  // Check required permissions
  if (requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every((permission) =>
      hasPermission(permission),
    );

    if (!hasAllPermissions) {
      return (
        fallback || (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center max-w-md">
              <div className="text-6xl mb-4">🔒</div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Access Denied
              </h1>
              <p className="text-gray-600">
                You don&apos;t have the required permissions to access this
                page.
              </p>
            </div>
          </div>
        )
      );
    }
  }

  return <>{children}</>;
}
